import Vue from 'vue';
import { has, filter, flatten, flow, keyBy, map, uniqBy, uniq } from 'lodash/fp';
import { compact, get, some, filter as lodashFilter } from 'lodash';
import Api from 'services/Api';
import Auth from 'services/Cognito';
import Http from 'services/Http';
import { getUser } from '../queries/account';
import { getAppsByAccount } from '../filters/account';
import { loadDefinitions } from '../queries/settings';
import i18n from 'services/i18n';
import { loadMomentLocale } from 'services/i18n';
import { isStatus2xx } from 'utils/http';
import { flags } from '../../config';

function reset() {
  const { locale } = i18n;

  return {
    userId: null,
    userType: null,
    appId: null,
    email: null,
    appsByAccount: {},
    locale,
    locales: [
      {
        value: 'en',
        label: 'English',
      },
    ],
    segmentDefinitions: null,
    momentDefinitions: null,
  };
}

const state = reset();

const actions = {
  /**
   * Initialize user account
   *
   * This action should only ever be called from Auth.login() or Auth.refresh() which will have set the Http JWT token
   *
   * @param   {object}  context     Vuex context object
   * @param   {string}  userId      A user id string, e.g. "5a7863bf538e420600000005"
   * @returns {Promise<T>}
   */ 
  async init({ commit, dispatch }, userId) {
    return dispatch('loadUser', userId)
      .then(res => {
        return dispatch('loadSettings');
      })
      .catch(err => {
        console.log(err);
        // https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/status
        if (err && err.status === 0) {
          return;
        }
        commit('reset');
      });
  },

  async loadBusinessUser({}, userId) {
    try {
      const res = await Api.get(`v2/business_users/${userId}`);

      if (isStatus2xx(get(res, 'status'))) {
        return get(res, 'body');
      }
    } catch (err) {
      console.log(err);
    }
  },

  async loadBusinessUserRoles({}, userId) {
    try {
      const res = await Api.get(`v2/business_users/${userId}/roles`);

      if (isStatus2xx(get(res, 'status'))) {
        return get(res, 'body.account_roles');
      }
    } catch (err) {
      console.log(err);
    }
  },

  /**
   * Load user data
   */
  async loadUser({ dispatch, commit, state }, userId) {
    console.log('Loading user data...');
    let user = null;
    const userType = state.userType;

    if (userType === 'business') {
      const userObj = await dispatch('loadBusinessUser', userId);

      if (userObj) {
        user = userObj;

        const roles = await dispatch('loadBusinessUserRoles', userId);

        if (roles) {
          user.account_roles = lodashFilter(roles, (r) => {
            return r.role === 'developer' || r.role === 'spectator'
          });
        }
      }
    } else {
      const res = await getUser(userId);

      user = get(res, 'body.data.user');
    }

    const appId = get(user, 'application.id');

    // check user
    if (
      !user ||
      !user.email ||
      !user.account_roles ||
      (userType === 'journeys' && !user.application)
    ) {
      throw new Error('No user, email, or account roles found');
    }

    // parse apps from account_roles
    let apps = flow(
      uniqBy('account_id'),
      filter(item => has('account.applications.slice', item)),
      map(item => item.account.applications.slice),
      flatten
    )(user.account_roles);

    // If the root app is not included in this list, add it
    if (!some(apps, app => app.id === appId)) {
      apps = compact([user.application, ...apps]);
    }

    const appsByAccount = getAppsByAccount(
      user.account_roles,
      user.application
    );

    // save user data
    commit('userId', userId);
    commit('appId', appId);
    commit('email', user.email);
    commit('appsByAccount', appsByAccount);

    // save apps
    commit('application/apps', apps, { root: true });
  },

  /**
   * Load server definitions
   *
   * Note these are stored to settings - not Vuex!
   *
   * @returns {*|PromiseLike<T>|Promise<T>}
   */
  loadSettings({ commit }) {
    return loadDefinitions().then(res => {
      const segments = get(res, 'body.data.segments') || [];
      const moments = get(res, 'body.data.moments') || [];
      const momentDefinitions = keyBy('id', moments.sort());
      const segmentDefinitions = keyBy('id', segments.sort());

      // store values as lookups (use _.values() to use as an array)
      commit('momentDefinitions', momentDefinitions);
      commit('segmentDefinitions', segmentDefinitions);
    });
  },

  async setLocale({ commit, dispatch }, locale) {
    commit('locale', locale);
    await dispatch('loadSettings');

    return locale;
  },
};

const getters = {

  /** Returns an array of appIds (across accounts) that the user is a developer|spectator of */
  getDeveloperOrSpectatorApps(state){
    const ids = Object.entries(state.appsByAccount).reduce((acc, [_accountId, account]) => {
      if (
        account.roles.includes('developer') ||
        account.roles.includes('spectator')
      ) {
        return [...acc, ...account.applications.map(app => app.id)]
      }
      return acc;
    }, [])

    return uniq(ids);    
  },
  isLoggedIn(state) {
    return !!state.userId;
  },
  locale(state) {
    return state.locale;
  },
  locales(state) {
    return state.locales;
  },
  segmentDefinitions(state) {
    return state.segmentDefinitions;
  },
  momentDefinitions(state) {
    return state.momentDefinitions;
  },
};

const mutations = {
  reset(state) {
    localStorage.removeItem('locale');
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('userType');
    Auth.signOut();
    Object.assign(state, reset());
  },

  userId(state, value) {
    state.userId = value;
    Vue.localStorage.set('userId', value);
  },

  userType(state, value) {
    state.userType = value;
  },

  appId(state, value) {
    state.appId = value;
  },

  email(state, value) {
    state.email = value;
  },

  appsByAccount(state, value) {
    state.appsByAccount = value;
  },

  locale(state, value) {
    state.locale = value;
    i18n.locale = value;
    Http.setLocale(value);
    loadMomentLocale(value);
  },

  momentDefinitions(state, value) {
    state.momentDefinitions = value;
  },

  segmentDefinitions(state, value) {
    state.segmentDefinitions = value;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
